import { track } from '../../../services/analytics/analytics'
import { IUserLeaveTypeResult, IGetUser } from '../../../types/custom-queries'
import { IDashboardLeaveRequest } from '@vacationtracker/shared/types/leave-request'

// Types for tracking events  
interface BaseEventProperties {
  timestamp?: string
  session_id?: string
  [key: string]: string | string[] | number | boolean | undefined | null
}

interface UserDashboardVisitedProperties extends BaseEventProperties {
  time_of_day: 'morning' | 'afternoon' | 'evening'
  day_of_week: string
  is_weekend: boolean
  is_mobile: boolean
  viewport_width: number
  viewport_height: number
  has_negative_balance: boolean
  has_pending_requests: boolean
  has_upcoming_leaves: boolean
  has_expiring_rollover: boolean
  leave_types_count: number
  team_size: number
  referrer: string
}

interface UserDashboardSessionCompleteProperties extends BaseEventProperties {
  session_duration_seconds: number
  scroll_depth_percentage: number
  interactions_count: number
  sections_viewed: string[]
  leave_types_viewed: string[]
  actions_taken: string[]
  buttons_clicked: string[]
  exit_action: 'navigation' | 'close_tab' | 'timeout' | 'error'
  next_page?: string
}

interface UserDashboardLoadTimeProperties extends BaseEventProperties {
  total_load_time_ms: number
  graphql_response_time_ms: number
  first_paint_ms: number
  time_to_interactive_ms: number
  data_size_kb: number
  cached_data_used: boolean
  balance_card_loaded_ms: number
  upcoming_leaves_loaded_ms: number
  team_availability_loaded_ms: number
  secondary_grid_loaded_ms: number
  connection_type: string
  browser_version: string
  is_slow_load: boolean
  had_layout_shift: boolean
  had_hydration_error: boolean
}

interface UserDashboardErrorProperties extends BaseEventProperties {
  error_type: 'network_error' | 'graphql_error' | 'permission_error' | 'parse_error' | 'component_error' | 'dashboard_load_error'
  error_message: string
  error_code?: string
  section_affected: string
  user_action_before_error?: string
  time_since_page_load_ms: number
  has_cached_data: boolean
  retry_attempted: boolean
  auto_recovery_possible: boolean
  browser_version: string
  device_info: string
  connection_status: 'online' | 'offline' | 'slow'
}

interface UserDashboardLeaveTypeSwitchedProperties extends BaseEventProperties {
  from_leave_type: string
  to_leave_type: string
  from_balance: number
  to_balance: number
  method: 'dropdown' | 'keyboard'
  available_types_count: number
  time_since_page_load_ms: number
  is_first_change: boolean
  changes_in_session: number
  balance_difference: number
  has_minutes_precision: boolean
  shortest_leave_interval: string
}

interface UserDashboardActionTakenProperties extends BaseEventProperties {
  action_type: 'button_click' | 'dropdown_change' | 'link_click'
  action_name: string
  element_type: 'button' | 'link' | 'dropdown' | 'card'
  section: 'header' | 'balance_card' | 'upcoming_leaves' | 'team_availability' | 'secondary_grid'
  current_leave_type: string
  current_balance: number
  is_loading: boolean
  scroll_position: number
  time_since_page_load_ms: number
  led_to_navigation: boolean
  destination_url?: string
}

interface UserDashboardTeamViewProperties extends BaseEventProperties {
  coverage_status_today: string
  coverage_status_tomorrow: string
  coverage_status_next_week: string
  team_size: number
  people_out_today: number
  people_out_tomorrow: number
  people_out_next_week: number
  visibility_level: string
  cards_viewed: string[]
  time_in_section_ms: number
}

interface UserDashboardEngagedProperties extends BaseEventProperties {
  milestone_type: 'time_spent' | 'interactions' | 'sections_explored'
  milestone_value: number
  current_section: string
  actions_completed: number
  data_freshness: 'fresh' | 'partial' | 'stale'
  scroll_depth: number
  unique_sections_visited: number
  meaningful_interactions: number
}

interface UserDashboardAlertSeenProperties extends BaseEventProperties {
  alert_type: 'rollover_expiry' | 'negative_balance' | 'no_approver' | 'probation_period'
  alert_severity: 'info' | 'warning' | 'error'
  alert_message: string
  action: 'viewed' | 'clicked' | 'dismissed'
  time_visible_ms: number
  days_until_expiry?: number
  amount_expiring?: number
  negative_amount?: number
  led_to_action: boolean
  follow_up_action?: string
}

interface UserDashboardBalanceCheckedProperties extends BaseEventProperties {
  leave_type_viewed: string
  balance_amount_range: string // '0-5', '5-15', '15-30', '30+'
  time_since_last_check_hours?: number
  has_minutes_precision: boolean
  is_negative: boolean
  is_unlimited: boolean
}

interface UserDashboardFeatureDiscoveredProperties extends BaseEventProperties {
  feature_name: 'part_day_times' | 'minutes_display' | 'dark_mode' | 'team_coverage' | 'rollover_warning'
  discovery_method: 'hover' | 'click' | 'scroll' | 'tooltip'
  time_to_discovery_ms: number
  session_count: number
  feature_visible: boolean
  feature_relevant: boolean
  interacted_after_discovery: boolean
  time_spent_with_feature_ms: number
}

// Session tracking state
class UserDashboardSessionTracker {
  private sessionStartTime: number = Date.now()
  private sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  private pageLoadTime: number = Date.now()
  private sectionsViewed: Set<string> = new Set()
  private leaveTypesViewed: Set<string> = new Set()
  private actionsTaken: Set<string> = new Set()
  private buttonsClicked: Set<string> = new Set()
  private interactionCount = 0
  private maxScrollDepth = 0
  private leaveTypeChanges = 0
  private isFirstLeaveTypeChange = true
  
  // Store timeout IDs and event handlers for cleanup
  private timeouts: NodeJS.Timeout[] = []
  private eventHandlers: { event: string; handler: () => void }[] = []

  // Helper methods
  private getTimeOfDay(): 'morning' | 'afternoon' | 'evening' {
    const hour = new Date().getHours()
    if (hour < 12) return 'morning'
    if (hour < 17) return 'afternoon'
    return 'evening'
  }

  private getDayOfWeek(): string {
    return new Date().toLocaleDateString('en-US', { weekday: 'long' })
  }

  private isWeekend(): boolean {
    const day = new Date().getDay()
    return day === 0 || day === 6
  }

  private isMobile(): boolean {
    return window.innerWidth < 768
  }

  private getConnectionType(): string {
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection
    return connection?.effectiveType || 'unknown'
  }

  private getBrowserVersion(): string {
    const userAgent = navigator.userAgent
    if (userAgent.includes('Chrome')) return `Chrome/${userAgent.split('Chrome/')[1]?.split(' ')[0] || 'unknown'}`
    if (userAgent.includes('Firefox')) return `Firefox/${userAgent.split('Firefox/')[1]?.split(' ')[0] || 'unknown'}`
    if (userAgent.includes('Safari')) return `Safari/${userAgent.split('Version/')[1]?.split(' ')[0] || 'unknown'}`
    return 'unknown'
  }

  private getDeviceInfo(): string {
    const { platform, userAgent } = navigator
    return `${platform} - ${userAgent.substring(0, 100)}`
  }

  private getBalanceRange(balance: number): string {
    if (balance < 0) return 'negative'
    if (balance <= 5) return '0-5'
    if (balance <= 15) return '5-15'
    if (balance <= 30) return '15-30'
    return '30+'
  }

  private updateScrollDepth(): void {
    const scrollDepth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100)
    this.maxScrollDepth = Math.max(this.maxScrollDepth, scrollDepth || 0)
  }

  // Public tracking methods
  trackVisited(user?: IGetUser, leaveTypes?: IUserLeaveTypeResult[], upcomingLeaves?: IDashboardLeaveRequest[]): void {
    const properties: UserDashboardVisitedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      time_of_day: this.getTimeOfDay(),
      day_of_week: this.getDayOfWeek(),
      is_weekend: this.isWeekend(),
      is_mobile: this.isMobile(),
      viewport_width: window.innerWidth,
      viewport_height: window.innerHeight,
      has_negative_balance: leaveTypes?.some(lt => lt.currentDays < 0) || false,
      has_pending_requests: (upcomingLeaves?.filter(l => l.status === 'OPEN')?.length || 0) > 0,
      has_upcoming_leaves: (upcomingLeaves?.length || 0) > 0,
      has_expiring_rollover: leaveTypes?.some(lt => lt.rolloverExpiryDate && new Date(lt.rolloverExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)) || false,
      leave_types_count: leaveTypes?.filter(lt => lt.isActive)?.length || 0,
      team_size: user?.team?.users?.length || 0,
      referrer: document.referrer || 'direct',
    }

    track('USER_DASHBOARD_VISITED', properties)
  }

  trackSessionComplete(exitAction: 'navigation' | 'close_tab' | 'timeout' | 'error' = 'navigation', nextPage?: string): void {
    this.updateScrollDepth()
    
    const properties: UserDashboardSessionCompleteProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      session_duration_seconds: Math.round((Date.now() - this.sessionStartTime) / 1000),
      scroll_depth_percentage: this.maxScrollDepth,
      interactions_count: this.interactionCount,
      sections_viewed: Array.from(this.sectionsViewed),
      leave_types_viewed: Array.from(this.leaveTypesViewed),
      actions_taken: Array.from(this.actionsTaken),
      buttons_clicked: Array.from(this.buttonsClicked),
      exit_action: exitAction,
      next_page: nextPage,
    }

    track('USER_DASHBOARD_SESSION_COMPLETE', properties)
  }

  trackLoadTime(performanceData: {
    totalLoadTime: number
    graphqlResponseTime: number
    firstPaintTime?: number
    timeToInteractive?: number
    dataSize?: number
    cachedDataUsed?: boolean
    componentLoadTimes?: {
      balanceCard?: number
      upcomingLeaves?: number
      teamAvailability?: number
      secondaryGrid?: number
    }
  }): void {
    const properties: UserDashboardLoadTimeProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      total_load_time_ms: performanceData.totalLoadTime,
      graphql_response_time_ms: performanceData.graphqlResponseTime,
      first_paint_ms: performanceData.firstPaintTime || 0,
      time_to_interactive_ms: performanceData.timeToInteractive || 0,
      data_size_kb: performanceData.dataSize || 0,
      cached_data_used: performanceData.cachedDataUsed || false,
      balance_card_loaded_ms: performanceData.componentLoadTimes?.balanceCard || 0,
      upcoming_leaves_loaded_ms: performanceData.componentLoadTimes?.upcomingLeaves || 0,
      team_availability_loaded_ms: performanceData.componentLoadTimes?.teamAvailability || 0,
      secondary_grid_loaded_ms: performanceData.componentLoadTimes?.secondaryGrid || 0,
      connection_type: this.getConnectionType(),
      browser_version: this.getBrowserVersion(),
      is_slow_load: performanceData.totalLoadTime > 2000,
      had_layout_shift: false, // Could be enhanced with actual CLS measurement
      had_hydration_error: false,
    }

    track('USER_DASHBOARD_LOAD_TIME', properties)
  }

  trackError(errorData: {
    errorType: 'network_error' | 'graphql_error' | 'permission_error' | 'parse_error' | 'component_error' | 'dashboard_load_error'
    errorMessage: string
    errorCode?: string
    sectionAffected: string
    userActionBeforeError?: string
    hasCachedData?: boolean
    retryAttempted?: boolean
    autoRecoveryPossible?: boolean
  }): void {
    const properties: UserDashboardErrorProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      error_type: errorData.errorType,
      error_message: errorData.errorMessage,
      error_code: errorData.errorCode,
      section_affected: errorData.sectionAffected,
      user_action_before_error: errorData.userActionBeforeError,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      has_cached_data: errorData.hasCachedData || false,
      retry_attempted: errorData.retryAttempted || false,
      auto_recovery_possible: errorData.autoRecoveryPossible || false,
      browser_version: this.getBrowserVersion(),
      device_info: this.getDeviceInfo(),
      connection_status: navigator.onLine ? 'online' : 'offline',
    }

    track('USER_DASHBOARD_ERROR', properties)
  }

  trackLeaveTypeSwitched(
    fromLeaveType: string,
    toLeaveType: string,
    fromBalance: number,
    toBalance: number,
    availableTypesCount: number,
    hasMinutesPrecision: boolean,
    shortestInterval: string
  ): void {
    this.leaveTypesViewed.add(toLeaveType)
    this.leaveTypeChanges++
    
    const properties: UserDashboardLeaveTypeSwitchedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      from_leave_type: fromLeaveType,
      to_leave_type: toLeaveType,
      from_balance: fromBalance,
      to_balance: toBalance,
      method: 'dropdown',
      available_types_count: availableTypesCount,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      is_first_change: this.isFirstLeaveTypeChange,
      changes_in_session: this.leaveTypeChanges,
      balance_difference: toBalance - fromBalance,
      has_minutes_precision: hasMinutesPrecision,
      shortest_leave_interval: shortestInterval,
    }

    this.isFirstLeaveTypeChange = false
    track('USER_DASHBOARD_LEAVE_TYPE_SWITCHED', properties)
  }

  trackActionTaken(actionData: {
    actionType: 'button_click' | 'dropdown_change' | 'link_click'
    actionName: string
    elementType: 'button' | 'link' | 'dropdown' | 'card'
    section: 'header' | 'balance_card' | 'upcoming_leaves' | 'team_availability' | 'secondary_grid'
    currentLeaveType: string
    currentBalance: number
    isLoading?: boolean
    ledToNavigation?: boolean
    destinationUrl?: string
  }): void {
    this.interactionCount++
    this.actionsTaken.add(actionData.actionName)
    this.sectionsViewed.add(actionData.section)
    this.updateScrollDepth()

    if (actionData.actionType === 'button_click') {
      this.buttonsClicked.add(actionData.actionName)
    }

    const properties: UserDashboardActionTakenProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      action_type: actionData.actionType,
      action_name: actionData.actionName,
      element_type: actionData.elementType,
      section: actionData.section,
      current_leave_type: actionData.currentLeaveType,
      current_balance: actionData.currentBalance,
      is_loading: actionData.isLoading || false,
      scroll_position: window.scrollY,
      time_since_page_load_ms: Date.now() - this.pageLoadTime,
      led_to_navigation: actionData.ledToNavigation || false,
      destination_url: actionData.destinationUrl,
    }

    track('USER_DASHBOARD_ACTION_TAKEN', properties)
  }

  trackTeamView(teamData: {
    coverageStatusToday: string
    coverageStatusTomorrow: string
    coverageStatusNextWeek: string
    teamSize: number
    peopleOutToday: number
    peopleOutTomorrow: number
    peopleOutNextWeek: number
    visibilityLevel: string
    cardsViewed: string[]
    timeInSectionMs: number
  }): void {
    this.sectionsViewed.add('team_availability')

    const properties: UserDashboardTeamViewProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      coverage_status_today: teamData.coverageStatusToday,
      coverage_status_tomorrow: teamData.coverageStatusTomorrow,
      coverage_status_next_week: teamData.coverageStatusNextWeek,
      team_size: teamData.teamSize,
      people_out_today: teamData.peopleOutToday,
      people_out_tomorrow: teamData.peopleOutTomorrow,
      people_out_next_week: teamData.peopleOutNextWeek,
      visibility_level: teamData.visibilityLevel,
      cards_viewed: teamData.cardsViewed,
      time_in_section_ms: teamData.timeInSectionMs,
    }

    track('USER_DASHBOARD_TEAM_VIEW', properties)
  }

  trackEngaged(milestoneData: {
    milestoneType: 'time_spent' | 'interactions' | 'sections_explored'
    milestoneValue: number
    currentSection: string
    actionsCompleted: number
    dataFreshness: 'fresh' | 'partial' | 'stale'
  }): void {
    this.updateScrollDepth()

    const properties: UserDashboardEngagedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      milestone_type: milestoneData.milestoneType,
      milestone_value: milestoneData.milestoneValue,
      current_section: milestoneData.currentSection,
      actions_completed: milestoneData.actionsCompleted,
      data_freshness: milestoneData.dataFreshness,
      scroll_depth: this.maxScrollDepth,
      unique_sections_visited: this.sectionsViewed.size,
      meaningful_interactions: this.interactionCount,
    }

    track('USER_DASHBOARD_ENGAGED', properties)
  }

  trackAlertSeen(alertData: {
    alertType: 'rollover_expiry' | 'negative_balance' | 'no_approver' | 'probation_period'
    alertSeverity: 'info' | 'warning' | 'error'
    alertMessage: string
    action: 'viewed' | 'clicked' | 'dismissed'
    timeVisibleMs: number
    daysUntilExpiry?: number
    amountExpiring?: number
    negativeAmount?: number
    ledToAction?: boolean
    followUpAction?: string
  }): void {
    const properties: UserDashboardAlertSeenProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      alert_type: alertData.alertType,
      alert_severity: alertData.alertSeverity,
      alert_message: alertData.alertMessage,
      action: alertData.action,
      time_visible_ms: alertData.timeVisibleMs,
      days_until_expiry: alertData.daysUntilExpiry,
      amount_expiring: alertData.amountExpiring,
      negative_amount: alertData.negativeAmount,
      led_to_action: alertData.ledToAction || false,
      follow_up_action: alertData.followUpAction,
    }

    track('USER_DASHBOARD_ALERT_SEEN', properties)
  }

  trackBalanceChecked(balanceData: {
    leaveTypeViewed: string
    balanceAmount: number
    timeSinceLastCheckHours?: number
    hasMinutesPrecision: boolean
    isNegative: boolean
    isUnlimited: boolean
  }): void {
    this.leaveTypesViewed.add(balanceData.leaveTypeViewed)

    const properties: UserDashboardBalanceCheckedProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      leave_type_viewed: balanceData.leaveTypeViewed,
      balance_amount_range: this.getBalanceRange(balanceData.balanceAmount),
      time_since_last_check_hours: balanceData.timeSinceLastCheckHours,
      has_minutes_precision: balanceData.hasMinutesPrecision,
      is_negative: balanceData.isNegative,
      is_unlimited: balanceData.isUnlimited,
    }

    track('USER_DASHBOARD_BALANCE_CHECKED', properties)
  }

  trackFeatureDiscovered(featureData: {
    featureName: 'part_day_times' | 'minutes_display' | 'dark_mode' | 'team_coverage' | 'rollover_warning'
    discoveryMethod: 'hover' | 'click' | 'scroll' | 'tooltip'
    timeToDiscoveryMs: number
    sessionCount: number
    featureVisible: boolean
    featureRelevant: boolean
    interactedAfterDiscovery: boolean
    timeSpentWithFeatureMs: number
  }): void {
    const properties: UserDashboardFeatureDiscoveredProperties = {
      timestamp: new Date().toISOString(),
      session_id: this.sessionId,
      feature_name: featureData.featureName,
      discovery_method: featureData.discoveryMethod,
      time_to_discovery_ms: featureData.timeToDiscoveryMs,
      session_count: featureData.sessionCount,
      feature_visible: featureData.featureVisible,
      feature_relevant: featureData.featureRelevant,
      interacted_after_discovery: featureData.interactedAfterDiscovery,
      time_spent_with_feature_ms: featureData.timeSpentWithFeatureMs,
    }

    track('USER_DASHBOARD_FEATURE_DISCOVERED', properties)
  }

  // Initialize tracking on page load
  init(): void {
    // Track initial page load
    this.pageLoadTime = Date.now()

    // Set up beforeunload tracking for session end
    const beforeUnloadHandler = () => {
      this.trackSessionComplete('close_tab')
    }
    window.addEventListener('beforeunload', beforeUnloadHandler)
    this.eventHandlers.push({ event: 'beforeunload', handler: beforeUnloadHandler })

    // Set up scroll tracking
    const scrollHandler = () => {
      this.updateScrollDepth()
    }
    window.addEventListener('scroll', scrollHandler)
    this.eventHandlers.push({ event: 'scroll', handler: scrollHandler })

    // Set up engagement milestones
    const timeout30s = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 30,
      currentSection: 'unknown',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
    }), 30000) // 30 seconds
    this.timeouts.push(timeout30s)

    const timeout2m = setTimeout(() => this.trackEngaged({
      milestoneType: 'time_spent',
      milestoneValue: 120,
      currentSection: 'unknown',
      actionsCompleted: this.interactionCount,
      dataFreshness: 'fresh',
    }), 120000) // 2 minutes
    this.timeouts.push(timeout2m)
  }

  // Cleanup method to cancel all timeouts and remove event listeners
  cleanup(): void {
    // Clear all timeouts
    this.timeouts.forEach(timeout => clearTimeout(timeout))
    this.timeouts = []

    // Remove all event listeners
    this.eventHandlers.forEach(({ event, handler }) => {
      window.removeEventListener(event, handler)
    })
    this.eventHandlers = []
  }
}

// Create and export singleton instance
export const dashboardTracker = new UserDashboardSessionTracker()

